import { Fragment, useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FunnelIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  UsersIcon,
  TagIcon,
  ChartBarIcon,
  SparklesIcon,
  BoltIcon,
} from "@heroicons/react/24/outline";
import { currencyFormat, percentageFormat } from "../helpers";

// Add field type definitions
const FIELD_TYPES = {
  Name: "string",
  Domain: "string",
  Industry: "string",
  Address: "string",
  ListSize: "number",
  AnnualRevenue: "number",
  OperationalCosts: "number",
  Debt: "number",
  ConversionRate: "number", // Decimal between 0-1
  FollowerCount: "number",
  AvgDealSize: "number",
  Technologies: "string",
  VisitToLeadConversionRate: "number",
  LeadToDealConversion: "number",
  IncreasedAnnualTraffic: "number",
};

// Define the mappings at the top of the file
const FILTER_MAPPINGS = {
  customerFocus: {
    B2B: "B2B",
    B2C: "B2C",
  },
  industries: {
    SaaS: "SaaS",
    "Software Dev": "Software Development",
    "Digital Agency": "Digital Agency",
    "E-commerce": "E-commerce",
    FinTech: "FinTech",
    "Healthcare Tech": "Healthcare Tech",
    EdTech: "EdTech",
    Legal: "Legal",
    "Real Estate": "Real Estate",
    Events: "Events",
    Other: "Other",
  },
  sizes: {
    "1 - 10": "1 - 10 employees",
    "11 - 50": "11 - 50 employees",
    "51 - 200": "51 - 200 employees",
    "201 - 500": "201 - 500 employees",
  },
  stages: {
    "Validating Idea": "Validating Idea",
    MVP: "MVP",
    Growth: "Growth",
    Scale: "Scale",
  },
  automationStatus: {
    "Ready to Use": "ready",
    "Coming Soon": "coming_soon",
    Requested: "requested",
  },
};

const FilterButton = ({
  isOpen,
  setIsOpen,
  companyData,
  onUpdateField,
  filters,
  setFilters,
  // Add new props for categories and stages
  categories,
  stages,
  selectedCategory,
  setSelectedCategory,
  selectedStage,
  setSelectedStage,
  showPrioritizedList,
  setShowPrioritizedList,
  handleStageChange,
  clearAllFilters,
}) => {
  const [editingField, setEditingField] = useState(null);

  const handleSave = (fieldName, value) => {
    // Don't allow saving calculated/read-only fields
    if (fieldName === "Profit") {
      console.error("Cannot save calculated field: Profit");
      return;
    }

    // Format value based on field type before saving
    const fieldType = FIELD_TYPES[fieldName];
    let formattedValue = value;

    if (fieldType === "number") {
      // Remove any non-numeric characters except decimal point
      const numericValue = value.toString().replace(/[^0-9.]/g, "");
      
      if (numericValue === "") {
        alert("Please enter a valid number");
        return;
      }
      
      formattedValue = Number(numericValue);

      // Validate number
      if (isNaN(formattedValue)) {
        alert("Please enter a valid number");
        return;
      }
      
      // For integer fields (ListSize, FollowerCount, IncreasedAnnualTraffic), round to integer
      if (fieldName === "ListSize" || 
          fieldName === "FollowerCount" || 
          fieldName === "IncreasedAnnualTraffic") {
        formattedValue = Math.round(formattedValue);
      }
      
      // For percentage fields, convert from percentage (5) to decimal (0.05) for storage
      if (fieldName === "ConversionRate" || 
          fieldName === "VisitToLeadConversionRate" || 
          fieldName === "LeadToDealConversionRate") {
        formattedValue = formattedValue / 100;
      }
    }

    if (onUpdateField) {
      onUpdateField(fieldName, formattedValue);
    } else {
      console.error("onUpdateField is not defined");
      alert("Error: Update handler not available");
      return;
    }
    
    setEditingField(null);
  };

  const handleFilterChange = (type, displayValue, isChecked) => {
    const airtableValue = FILTER_MAPPINGS[type][displayValue];
    setFilters((prev) => {
      // Initialize the array if it doesn't exist
      const currentArray = prev[type] || [];
      return {
        ...prev,
        [type]: isChecked
          ? [...currentArray, airtableValue]
          : currentArray.filter((item) => item !== airtableValue),
      };
    });
  };

  const EditableField = ({ label, value, rawValue, fieldName, isReadOnly = false }) => {
    const fieldType = FIELD_TYPES[fieldName];
    
    // Get the initial edit value based on field type
    const getInitialEditValue = () => {
      if (rawValue === undefined || rawValue === null || rawValue === "") {
        return "";
      }
      
      // For percentage fields, convert from decimal (0.05) to percentage (5) for editing
      if (fieldName === "ConversionRate" || 
          fieldName === "VisitToLeadConversionRate" || 
          fieldName === "LeadToDealConversionRate") {
        return (Number(rawValue) * 100).toString();
      }
      
      // For number fields, use the raw numeric value
      if (fieldType === "number") {
        return rawValue?.toString() || "";
      }
      
      // For string fields, use the raw value
      return rawValue?.toString() || "";
    };

    const [editValue, setEditValue] = useState(getInitialEditValue());

    // Update editValue when entering edit mode or when rawValue changes
    useEffect(() => {
      if (editingField === fieldName) {
        setEditValue(getInitialEditValue());
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editingField, fieldName, rawValue, fieldType]);

    const handleInputChange = (e) => {
      let newValue = e.target.value;

      if (fieldType === "number") {
        // Allow only numbers and decimal point while typing
        newValue = newValue.replace(/[^0-9.]/g, "");
      }

      setEditValue(newValue);
    };

    if (editingField === fieldName) {
      return (
        <div>
          <div className="text-xs text-gray-500">{label}</div>
          <div className="flex gap-2">
            <input
              type="text"
              value={editValue}
              onChange={handleInputChange}
              className="text-sm border rounded px-2 py-1 flex-1"
              placeholder={fieldName === "ConversionRate" || 
                          fieldName === "VisitToLeadConversionRate" || 
                          fieldName === "LeadToDealConversionRate" 
                          ? "Enter percentage (e.g., 5 for 5%)" 
                          : ""}
            />
            <button
              onClick={() => handleSave(fieldName, editValue)}
              className="text-xs text-blue-600 hover:text-blue-800"
            >
              Save
            </button>
            <button
              onClick={() => setEditingField(null)}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              Cancel
            </button>
          </div>
        </div>
      );
    }

    return (
      <div>
        <div className="text-xs text-gray-500">{label}</div>
        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-700">
            {value || "Not specified"}
          </div>
          {!isReadOnly && (
            <button
              onClick={() => setEditingField(fieldName)}
              className="text-xs text-gray-400 hover:text-blue-600"
            >
              Edit
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`bg-white p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group flex items-center justify-center ${
          isOpen ? "ring-2 ring-stone-600" : ""
        }`}
      >
        <FunnelIcon className="h-6 w-6 text-stone-600 group-hover:scale-110 transition-transform duration-200" />
      </button>

      {/* YouTube-style Modal Overlay - Fixed to viewport */}
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-xl border border-gray-200 w-full max-w-6xl max-h-[85vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                Filters & Prioritization
              </h2>
              <div className="flex items-center gap-3">
                <button
                  onClick={clearAllFilters}
                  className="text-sm text-gray-600 hover:text-gray-800 transition-colors px-3 py-1 rounded-md hover:bg-gray-100"
                >
                  Clear All
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {/* Filter Content */}
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 pr-0">
                {/* Customer Focus Filter */}
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide flex items-center gap-2">
                    <UserGroupIcon className="h-4 w-4" />
                    Customer Focus
                  </h3>
                  <div className="space-y-2">
                    {Object.entries(FILTER_MAPPINGS.customerFocus).map(
                      ([display, value]) => (
                        <label
                          key={value}
                          className="flex items-center cursor-pointer hover:bg-gray-50 p-1 rounded"
                        >
                          <input
                            type="checkbox"
                            checked={filters.customerFocus.includes(value)}
                            onChange={(e) =>
                              handleFilterChange(
                                "customerFocus",
                                display,
                                e.target.checked
                              )
                            }
                            className="rounded border-gray-300 text-black focus:ring-black"
                          />
                          <span className="ml-2 text-sm text-gray-700">
                            {display}
                          </span>
                        </label>
                      )
                    )}
                  </div>
                </div>

                {/* Industry Filter */}
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide flex items-center gap-2">
                    <BuildingOfficeIcon className="h-4 w-4" />
                    Industry
                  </h3>
                  <div className="space-y-2">
                    {Object.entries(FILTER_MAPPINGS.industries).map(
                      ([display, value]) => (
                        <label
                          key={value}
                          className="flex items-center cursor-pointer hover:bg-gray-50 p-1 rounded"
                        >
                          <input
                            type="checkbox"
                            checked={filters.industries.includes(value)}
                            onChange={(e) =>
                              handleFilterChange(
                                "industries",
                                display,
                                e.target.checked
                              )
                            }
                            className="rounded border-gray-300 text-black focus:ring-black"
                          />
                          <span className="ml-2 text-sm text-gray-700">
                            {display}
                          </span>
                        </label>
                      )
                    )}
                  </div>
                </div>

                {/* Company Size Filter */}
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide flex items-center gap-2">
                    <UsersIcon className="h-4 w-4" />
                    Company Size
                  </h3>
                  <div className="space-y-2">
                    {Object.entries(FILTER_MAPPINGS.sizes).map(
                      ([display, value]) => (
                        <label
                          key={value}
                          className="flex items-center cursor-pointer hover:bg-gray-50 p-1 rounded"
                        >
                          <input
                            type="checkbox"
                            checked={filters.sizes.includes(value)}
                            onChange={(e) =>
                              handleFilterChange(
                                "sizes",
                                display,
                                e.target.checked
                              )
                            }
                            className="rounded border-gray-300 text-black focus:ring-black"
                          />
                          <span className="ml-2 text-sm text-gray-700">
                            {display}
                          </span>
                        </label>
                      )
                    )}
                  </div>
                </div>

                {/* Category Filter */}
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide flex items-center gap-2">
                    <TagIcon className="h-4 w-4" />
                    Category
                  </h3>
                  <div className="space-y-2">
                    {categories?.map((category) => (
                      <label
                        key={category.id}
                        className="flex items-center cursor-pointer hover:bg-gray-50 p-1 rounded"
                      >
                        <input
                          type="radio"
                          name="category"
                          checked={selectedCategory === category.id}
                          onChange={() => setSelectedCategory(category.id)}
                          className="border-gray-300 text-black focus:ring-black"
                        />
                        <span className="ml-2 text-sm text-gray-700">
                          {category.name}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Company Stage Filter */}
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide flex items-center gap-2">
                    <ChartBarIcon className="h-4 w-4" />
                    Company Stage
                  </h3>
                  <div className="space-y-2">
                    {Object.entries(FILTER_MAPPINGS.stages).map(
                      ([display, value]) => (
                        <label
                          key={value}
                          className="flex items-center cursor-pointer hover:bg-gray-50 p-1 rounded"
                        >
                          <input
                            type="checkbox"
                            checked={filters.stages.includes(value)}
                            onChange={(e) =>
                              handleFilterChange(
                                "stages",
                                display,
                                e.target.checked
                              )
                            }
                            className="rounded border-gray-300 text-black focus:ring-black"
                          />
                          <span className="ml-2 text-sm text-gray-700">
                            {display}
                          </span>
                        </label>
                      )
                    )}
                  </div>

                  {/* Automation Status Filter */}
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide flex items-center gap-2">
                    <BoltIcon className="h-4 w-4" />
                    Status
                  </h3>
                  <div className="space-y-2">
                    {Object.entries(FILTER_MAPPINGS.automationStatus).map(
                      ([display, value]) => (
                        <label
                          key={value}
                          className="flex items-center cursor-pointer hover:bg-gray-50 p-1 rounded"
                        >
                          <input
                            type="checkbox"
                            checked={
                              filters.automationStatus?.includes(value) || false
                            }
                            onChange={(e) =>
                              handleFilterChange(
                                "automationStatus",
                                display,
                                e.target.checked
                              )
                            }
                            className="rounded border-gray-300 text-black focus:ring-black"
                          />
                          <span className="ml-2 text-sm text-gray-700">
                            {display}
                          </span>
                        </label>
                      )
                    )}
                  </div>
                </div>

                {/* Prioritization Filter */}
                <div className="space-y-3 border-l border-gray-300 pl-6">
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide flex items-center gap-2">
                    <SparklesIcon className="h-4 w-4" />
                    Prioritization
                  </h3>
                  <div className="space-y-2">
                    <label className="flex items-center cursor-pointer hover:bg-gray-50 p-1 rounded">
                      <input
                        type="radio"
                        name="prioritization"
                        checked={showPrioritizedList}
                        onChange={() => {
                          setShowPrioritizedList(true);
                          setSelectedStage(null);
                        }}
                        className="border-gray-300 text-gray-900 focus:ring-gray-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        Team Queue ✨📈
                      </span>
                    </label>
                    <label className="flex items-center cursor-pointer hover:bg-gray-50 p-1 rounded">
                      <input
                        type="radio"
                        name="prioritization"
                        checked={
                          !showPrioritizedList && selectedStage === "all"
                        }
                        onChange={() => {
                          setShowPrioritizedList(false);
                          handleStageChange("all");
                        }}
                        className="border-gray-300 text-gray-900 focus:ring-gray-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        All Automations
                      </span>
                    </label>
                    <label className="flex items-center cursor-pointer hover:bg-gray-50 p-1 rounded">
                      <input
                        type="radio"
                        name="prioritization"
                        checked={
                          !showPrioritizedList && selectedStage === "popular"
                        }
                        onChange={() => {
                          setShowPrioritizedList(false);
                          handleStageChange("popular");
                        }}
                        className="border-gray-300 text-gray-900 focus:ring-gray-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        Popular
                      </span>
                    </label>
                    <label className="flex items-center cursor-pointer hover:bg-gray-50 p-1 rounded">
                      <input
                        type="radio"
                        name="prioritization"
                        checked={
                          !showPrioritizedList && selectedStage === "value_menu"
                        }
                        onChange={() => {
                          setShowPrioritizedList(false);
                          handleStageChange("value_menu");
                        }}
                        className="border-gray-300 text-gray-900 focus:ring-gray-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        Value Menu 🍔🍟
                      </span>
                    </label>
                    <label className="flex items-center cursor-pointer hover:bg-gray-50 p-1 rounded">
                      <input
                        type="radio"
                        name="prioritization"
                        checked={
                          !showPrioritizedList && selectedStage === "value_rank"
                        }
                        onChange={() => {
                          setShowPrioritizedList(false);
                          handleStageChange("value_rank");
                        }}
                        className="border-gray-300 text-gray-900 focus:ring-gray-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        Value Rank ✨📈
                      </span>
                    </label>
                    <label className="flex items-center cursor-pointer hover:bg-gray-50 p-1 rounded">
                      <input
                        type="radio"
                        name="prioritization"
                        checked={
                          !showPrioritizedList && selectedStage === "start_grow"
                        }
                        onChange={() => {
                          setShowPrioritizedList(false);
                          handleStageChange("start_grow");
                        }}
                        className="border-gray-300 text-gray-900 focus:ring-gray-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        Start&Grow 🚀💼
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Company Data Section */}
              <div className="mt-8 border-t border-gray-200 pt-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Prioritize AI & Automations by Est $ Earnings With Your
                    Company Info
                  </h3>
                  <Link
                    to="/onboarding?step=0"
                    className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-gray-800 hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-800 transition-colors duration-200"
                  >
                    <SparklesIcon className="h-4 w-4 mr-2" />
                    Complete Onboarding
                  </Link>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <EditableField
                    label="Company Name"
                    value={companyData?.fields?.Name}
                    rawValue={companyData?.fields?.Name}
                    fieldName="Name"
                  />
                  <EditableField
                    label="Company Website"
                    value={companyData?.fields?.Domain}
                    rawValue={companyData?.fields?.Domain}
                    fieldName="Domain"
                  />
                  <EditableField
                    label="Industry"
                    value={companyData?.fields?.Industry}
                    rawValue={companyData?.fields?.Industry}
                    fieldName="Industry"
                  />
                  <EditableField
                    label="Location"
                    value={companyData?.fields?.Address}
                    rawValue={companyData?.fields?.Address}
                    fieldName="Address"
                  />
                  <EditableField
                    label="Email List Size"
                    value={companyData?.fields?.ListSize}
                    rawValue={companyData?.fields?.ListSize}
                    fieldName="ListSize"
                  />
                  <EditableField
                    label="Annual Revenue"
                    value={currencyFormat(companyData?.fields?.AnnualRevenue)}
                    rawValue={companyData?.fields?.AnnualRevenue}
                    fieldName="AnnualRevenue"
                  />
                  <EditableField
                    label="Operational Costs"
                    value={currencyFormat(
                      companyData?.fields?.OperationalCosts
                    )}
                    rawValue={companyData?.fields?.OperationalCosts}
                    fieldName="OperationalCosts"
                  />
                  <EditableField
                    label="Profit"
                    value={currencyFormat(
                      companyData?.fields?.AnnualRevenue -
                        companyData?.fields?.OperationalCosts
                    )}
                    rawValue={
                      companyData?.fields?.AnnualRevenue -
                        companyData?.fields?.OperationalCosts
                    }
                    fieldName="Profit"
                    isReadOnly={true}
                  />
                  <EditableField
                    label="Debt"
                    value={currencyFormat(companyData?.fields?.Debt)}
                    rawValue={companyData?.fields?.Debt}
                    fieldName="Debt"
                  />
                  <EditableField
                    label="Visitor to Deal Conversion Rate"
                    value={percentageFormat(
                      companyData?.fields?.ConversionRate
                    )}
                    rawValue={companyData?.fields?.ConversionRate}
                    fieldName="ConversionRate"
                  />
                  <EditableField
                    label="Follower Count"
                    value={companyData?.fields?.FollowerCount}
                    rawValue={companyData?.fields?.FollowerCount}
                    fieldName="FollowerCount"
                  />
                  <EditableField
                    label="Avg Deal Size"
                    value={currencyFormat(companyData?.fields?.AvgDealSize)}
                    rawValue={companyData?.fields?.AvgDealSize}
                    fieldName="AvgDealSize"
                  />
                  <EditableField
                    label="Visit to Lead Conversion"
                    value={percentageFormat(
                      companyData?.fields?.VisitToLeadConversionRate
                    )}
                    rawValue={companyData?.fields?.VisitToLeadConversionRate}
                    fieldName="VisitToLeadConversionRate"
                  />
                  <EditableField
                    label="Lead to Deal Conversion"
                    value={percentageFormat(
                      companyData?.fields?.LeadToDealConversionRate
                    )}
                    rawValue={companyData?.fields?.LeadToDealConversionRate}
                    fieldName="LeadToDealConversionRate"
                  />
                  <EditableField
                    label="Increased Annual Traffic"
                    value={companyData?.fields?.IncreasedAnnualTraffic}
                    rawValue={companyData?.fields?.IncreasedAnnualTraffic}
                    fieldName="IncreasedAnnualTraffic"
                  />
                  <EditableField
                    label="Technologies"
                    value={companyData?.fields?.Technologies}
                    rawValue={companyData?.fields?.Technologies}
                    fieldName="Technologies"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FilterButton;
